import { Dispatch, SetStateAction } from "react";
import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
  RequestResult,
} from "../../lib";
import { FetchDuration } from "../../model/dto";
import { CorrelationConfig, Variable } from "../../shared/analysis";

import { doREQUEST_ERROR, doREQUEST_COMPLETE } from "../state";
import { getToken } from "./token_fetcher";

export const REQUEST_ANALYTIC_GRAPH_CORRELATION_MULTIPLE_GROUP_MULTIPLE_DURATION_FETCH =
  "REQUEST_ANALYTIC_GRAPH_CORRELATION_MULTIPLE_GROUP_MULTIPLE_DURATION_FETCH";
export const doREQUEST_ANALYTIC_GRAPH_CORRELATION_MULTIPLE_GROUP_MULTIPLE_DURATION_FETCH =
  createDoRequestAction(REQUEST_ANALYTIC_GRAPH_CORRELATION_MULTIPLE_GROUP_MULTIPLE_DURATION_FETCH);

export const requestAnalyticGraphCorrelationMultipleGroupMultipleDurationFetchActionCreators = {
  doREQUEST_ANALYTIC_GRAPH_CORRELATION_MULTIPLE_GROUP_MULTIPLE_DURATION_FETCH,
};

export function* requestAnalyticGraphCorrelationMultipleGroupMultipleDurationFetch({
  payload,
  type,
  setRequestResult,
}: {
  payload: {
    requestId: string;
    groups: Record<string, string[]>;
    yymmddDurations: Record<string, FetchDuration>;
    variables: Variable[];
    config?: CorrelationConfig;
  };
  type: string;
  setRequestResult: Dispatch<SetStateAction<RequestResult | null>>;
}): SagaIterator {
  debug("saga*requestAnalyticGraphCorrelationMultipleGroupMultipleDurationFetch");

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        groups: v
          .object<Record<string, string[]>>()
          .pattern(v.string(), v.array().items(v.string().uuid()).min(3))
          .min(2)
          .required(),
        yymmddDurations: v
          .object<Record<string, FetchDuration>>()
          .pattern(
            v.string(),
            v.object({
              yymmddIndexStart: v.number().integer().positive().required(),
              yymmddIndexEnd: v.number().integer().positive().required(),
            }),
          )
          .min(2)
          .required(),
        variables: v
          .array()
          .min(2)
          .max(4)
          .items(
            v
              .object({
                identifier: v.string().exist(),
              })
              .unknown(true)
              .required(),
          )
          .required(),
        config: v
          .object({
            color_discreate_sequence: v.array().items(v.string()).optional(),
          })
          .optional(),
      }),
    });
  } catch (error) {
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_ANALYTIC_GRAPH_CORRELATION_MULTIPLE_GROUP_MULTIPLE_DURATION_FETCH,
          requestId: payload.requestId,
          error,
        },
        setRequestResult,
      ),
    );
  }

  const { groups, yymmddDurations, variables, config } = payload;
  const accessToken = yield call(getToken);

  const result = yield request({
    method: "post",
    url: `${API_URL}/v1/web/analytic-graph-correlation-multiple-group-multiple-duration-fetch`,
    accessToken,
    data: {
      groups,
      yymmddDurations,
      variables,
      config,
    },
  });

  if (!result.success || result.error) {
    const error = result.error || new Error("request did not succeed");
    debug(error);
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_ANALYTIC_GRAPH_CORRELATION_MULTIPLE_GROUP_MULTIPLE_DURATION_FETCH,
          fromPayload: payload,
          requestId: payload.requestId,
          url: "/v1/web/analytic-graph-correlation-multiple-group-multiple-duration-fetch",
          ...result,
          error,
        },
        setRequestResult,
      ),
    );
  }

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: REQUEST_ANALYTIC_GRAPH_CORRELATION_MULTIPLE_GROUP_MULTIPLE_DURATION_FETCH,
        fromPayload: payload,
        requestId: payload.requestId,
        ...result,
      },
      setRequestResult,
    ),
  );
}

export function* analyticGraphCorrelationMultipleGroupMultipleDurationFetchSaga() {
  yield takeEvery(
    REQUEST_ANALYTIC_GRAPH_CORRELATION_MULTIPLE_GROUP_MULTIPLE_DURATION_FETCH,
    requestAnalyticGraphCorrelationMultipleGroupMultipleDurationFetch,
  );
}
